import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../assets/css/styles.min.css';

const AddService = () => {
    const [service, setService] = useState({
        title: '',
        description: '',
        price: '',
        status: 'active',
    });
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { id, value } = e.target;
        setService({
            ...service,
            [id]: value,
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            setAlertMessage({
                type: 'danger',
                text: 'Invalid file type. Please upload JPEG, PNG, JPG, or GIF image.'
            });
            return;
        }

        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            setAlertMessage({
                type: 'danger',
                text: 'Image size too large. Maximum size is 2MB.'
            });
            return;
        }

        setImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setAlertMessage(null); // Clear previous alerts

        const formData = new FormData();
        formData.append('title', service.title.trim());
        formData.append('description', service.description.trim());
        formData.append('price', service.price.toString().trim());
        formData.append('status', service.status);

        if (image) {
            formData.append('image', image);
        }

        try {
            const response = await axios.post(
                'https://api.codecircle.co/api/site-services', 
                formData, 
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Accept': 'application/json'
                    }
                }
            );

            setAlertMessage({ 
                type: 'success', 
                text: 'Service added successfully!' 
            });

            setTimeout(() => {
                navigate('/service-management');
            }, 1500);
        } catch (error) {
            console.error('Submission error:', error.response?.data);
            
            let errorMessage = 'An error occurred while adding the service.';
            
            if (error.response?.status === 422 && error.response?.data?.errors) {
                // Handle Laravel validation errors
                errorMessage = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                    .join('\n');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            setAlertMessage({ 
                type: 'danger', 
                text: errorMessage 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page-wrapper" id="main-wrapper" data-layout="vertical" data-navbarbg="skin6" data-sidebartype="full" data-sidebar-position="fixed" data-header-position="fixed">
            <div className="body-wrapper">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-lg-9">
                            <div className="card border-0 shadow-lg" style={{ marginTop: "40px", background: 'linear-gradient(to right bottom, #ffffff, #fff5f5)' }}>
                                <div className="card-body p-5">
                                    <div className="text-center mb-4">
                                        <div className="feature-icon d-inline-flex align-items-center justify-content-center bg-gradient fs-2 mb-3">
                                            <i className="fas fa-cog" style={{ color: '#ff4c4c', fontSize: '2.5rem' }}></i>
                                        </div>
                                        <h3 className="fw-bold" style={{ color: '#ff4c4c' }}>Add New Service</h3>
                                        <p className="text-muted">Fill out the form below to add a new service</p>
                                    </div>

                                    {alertMessage && (
                                        <div className={`alert alert-${alertMessage.type} alert-dismissible fade show`} role="alert">
                                            {alertMessage.text.split('\n').map((line, i) => (
                                                <p key={i} className="mb-0">{line}</p>
                                            ))}
                                            <button 
                                                type="button" 
                                                className="btn-close" 
                                                onClick={() => setAlertMessage(null)}
                                            ></button>
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit} className="mt-4" noValidate>
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <div className="form-floating mb-3">
                                                    <input
                                                        type="text"
                                                        className="form-control border-0 shadow-sm"
                                                        id="title"
                                                        value={service.title}
                                                        onChange={handleChange}
                                                        placeholder="Service Title"
                                                        required
                                                        style={{ backgroundColor: 'rgba(255, 76, 76, 0.03)' }}
                                                    />
                                                    <label htmlFor="title" className="text-muted">
                                                        <i className="fas fa-tag me-2"></i>Service Title
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-floating mb-3">
                                                    <textarea
                                                        className="form-control border-0 shadow-sm"
                                                        id="description"
                                                        value={service.description}
                                                        onChange={handleChange}
                                                        placeholder="Service Description"
                                                        style={{ height: '120px', backgroundColor: 'rgba(255, 76, 76, 0.03)' }}
                                                        required
                                                    ></textarea>
                                                    <label htmlFor="description" className="text-muted">
                                                        <i className="fas fa-align-left me-2"></i>Service Description
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-floating mb-3">
                                                    <input
                                                        type="text"
                                                        className="form-control border-0 shadow-sm"
                                                        id="price"
                                                        value={service.price}
                                                        onChange={handleChange}
                                                        placeholder="Service Price"
                                                        required
                                                        style={{ backgroundColor: 'rgba(255, 76, 76, 0.03)' }}
                                                    />
                                                    <label htmlFor="price" className="text-muted">
                                                        <i className="fas fa-dollar-sign me-2"></i>Service Price
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-floating mb-3">
                                                    <select
                                                        className="form-select border-0 shadow-sm"
                                                        id="status"
                                                        value={service.status}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ backgroundColor: 'rgba(255, 76, 76, 0.03)' }}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                    <label htmlFor="status" className="text-muted">
                                                        <i className="fas fa-toggle-on me-2"></i>Service Status
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label htmlFor="image" className="form-label text-muted">
                                                        <i className="fas fa-image me-2"></i>Service Image
                                                    </label>
                                                    <input
                                                        type="file"
                                                        className="form-control border-0 shadow-sm"
                                                        id="image"
                                                        onChange={handleImageChange}
                                                        accept=".jpeg,.jpg,.png,.gif"
                                                        required
                                                        style={{ backgroundColor: 'rgba(255, 76, 76, 0.03)' }}
                                                    />
                                                    <small className="text-muted">Accepted formats: JPEG, JPG, PNG, GIF (Max 2MB)</small>
                                                </div>

                                                {imagePreview && (
                                                    <div className="mb-3 text-center">
                                                        <div className="border rounded p-2 d-inline-block">
                                                            <img
                                                                src={imagePreview}
                                                                alt="Preview"
                                                                style={{ maxHeight: '200px', maxWidth: '100%' }}
                                                                className="img-fluid"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-center mt-4">
                                            <button 
                                                type="submit" 
                                                className="btn btn-lg px-5 py-3 rounded-pill shadow-sm"
                                                style={{ 
                                                    background: 'linear-gradient(to right, #ff4c4c, #ff6666)',
                                                    color: '#fff',
                                                    border: 'none',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Adding Service...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-plus-circle me-2"></i>
                                                        Add Service
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddService;